import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Sparkles,
  ShoppingCart,
  ClipboardList,
  Users,
  Pill,
  AlertTriangle,
  AlertOctagon,
  AlertCircle,
  Tags,
  Truck,
  Receipt,
  DollarSign,
  Wallet,
  UserCog,
  BarChart3,
  Settings,
  LogOut,
  X,
  UserPlus,
  RotateCcw,
  Download,
  PanelLeftOpen,
  PanelLeftClose,
  CalendarCheck,
  CalendarRange,
} from 'lucide-react';
import useInstallPrompt from '../hooks/useInstallPrompt';
import { useSettings } from '../context/SettingsContext';
import PharmacyLogo from './PharmacyLogo';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationsContext';

export default function Sidebar({ isOpen, isPinnedOpen, onTogglePinned, onClose }) {
  const location = useLocation();
  const { install, canInstall } = useInstallPrompt();
  const { user } = useAuth();
  const { settings } = useSettings();
  const isCashier = user?.role === 'cashier';
  const aiEnabled = settings?.enable_ai_assistant === '1' || settings?.enable_ai_assistant === true;
  const { counts } = useNotifications();

const navGroups = isCashier ? [
    {
      label: 'SALES',
      items: [
        { icon: ShoppingCart, label: 'POS / New Sale', path: '/pos' },
        { icon: ClipboardList, label: 'Sales History', path: '/sales' },
        { icon: Users, label: 'Customers', path: '/customers' },
      ],
    },
    {
      label: 'INVENTORY',
      items: [
        { icon: Pill, label: 'Medicines', path: '/medicines' },
      ],
    },
    {
      label: 'REFUNDS',
      items: [
        { icon: RotateCcw, label: 'Customer Refunds', path: '/refunds/customer' },
      ],
    },
  ] : [
    {
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
        ...(aiEnabled ? [{ icon: Sparkles, label: 'AI Assistant', path: '/ai-assistant' }] : []),
      ],
    },
    {
      label: 'SALES',
      items: [
        { icon: ShoppingCart,   label: 'POS / New Sale',  path: '/pos' },
        { icon: ClipboardList,  label: 'Sales History',   path: '/sales' },
        { icon: CalendarCheck,  label: 'Daily Closing',   path: '/daily-closing' },
        { icon: CalendarRange,  label: 'Monthly Closing', path: '/monthly-closing' },
        { icon: Users,          label: 'Customers',       path: '/customers' },
      ],
    },
    {
      label: 'INVENTORY',
      items: [
        { icon: Pill,           label: 'Medicines',       path: '/medicines' },
        { icon: AlertTriangle,  label: 'Expiry Alerts',   path: '/expiry-alerts', badge: counts.expiry > 0 ? String(counts.expiry) : null },
        { icon: AlertOctagon,   label: 'Low Stock',       path: '/low-stock', badge: counts.low_stock > 0 ? String(counts.low_stock) : null },
        { icon: AlertCircle,    label: 'Damage Alerts',   path: '/damage-alerts', badge: counts.damage > 0 ? String(counts.damage) : null },
        { icon: Tags,           label: 'Categories',      path: '/categories' },
      ],
    },
    {
      label: 'PROCUREMENT',
      items: [
        { icon: Receipt,  label: 'Purchases',   path: '/purchases' },
        { icon: Truck,   label: 'Suppliers',   path: '/suppliers' },
      ],
    },
    {
      label: 'REFUNDS',
      items: [
        { icon: RotateCcw,  label: 'Customer Refunds',  path: '/refunds/customer' },
        { icon: RotateCcw,  label: 'Supplier Refunds',  path: '/refunds/supplier' },
      ],
    },
{
      label: 'ADMIN',
      items: [
        { icon: DollarSign,  label: 'Expenses',    path: '/expenses' },
        { icon: Wallet,      label: 'Accounting',  path: '/accounting' },
        { icon: UserPlus,    label: 'Staff',       path: '/staff' },
        { icon: UserCog,     label: 'Users',       path: '/users' },
        { icon: BarChart3,   label: 'Reports',     path: '/reports' },
        { icon: Settings,    label: 'Settings',    path: '/settings' },
      ],
    },
  ];

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 transition-opacity lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        data-pinned={isPinnedOpen}
        className={clsx(
          'app-sidebar fixed inset-y-0 left-0 z-50 flex w-60 flex-col overflow-visible bg-white border-r border-gray-200 transition-[width,transform] duration-300 ease-in-out',
          'lg:translate-x-0 lg:static lg:z-50',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* ── Brand ── */}
        <div className="relative flex h-16 items-center gap-3 border-b border-gray-100 px-5">
          <PharmacyLogo logo={settings?.pharmacy_logo} className="h-9 w-9 rounded-lg" imageClassName="h-9 w-9 rounded-lg object-contain" />
          <div className="sidebar-brand-copy min-w-0">
            <span className="text-[15px] font-bold text-gray-900 leading-tight block">{settings?.pharmacy_name || 'Gaza Pharmacy'}</span>
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Pharmacy POS</span>
          </div>
          <button
            type="button"
            onClick={onTogglePinned}
            title={isPinnedOpen ? 'Collapse sidebar' : 'Keep sidebar open'}
            aria-label={isPinnedOpen ? 'Collapse sidebar' : 'Keep sidebar open'}
            className="sidebar-pin-toggle absolute -right-3 top-5 z-10 rounded-full border-2 border-gray-200 bg-white p-1.5 text-gray-600 shadow-md transition-colors hover:bg-emerald-50 hover:text-emerald-600"
          >
            {isPinnedOpen ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeftOpen className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={onClose}
            className="ml-auto rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Navigation ── */}
        <nav className="flex-1 overflow-y-auto px-3 py-4" role="navigation" aria-label="Main navigation">
          {navGroups.map((group, gi) => (
            <div key={gi} className={clsx(gi > 0 && 'mt-6')}>
              {group.label && (
                <p className="sidebar-section-label mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  {group.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const isActive =
                    item.path === '/'
                      ? location.pathname === '/'
                      : location.pathname.startsWith(item.path);

                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={onClose}
                        className={clsx(
                          'sidebar-nav-link group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
                          isActive
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                      >
                        <item.icon
                          className={clsx(
                            'h-[18px] w-[18px] flex-shrink-0 transition-colors',
                            isActive
                              ? 'text-emerald-600'
                              : 'text-gray-400 group-hover:text-gray-600'
                          )}
                        />
                        <span className="sidebar-label flex-1 truncate">{item.label}</span>
                        {item.badge ? (
                          <span className={`sidebar-badge sidebar-label rounded-full px-2 py-0.5 text-[10px] font-bold leading-none ${
                            item.path === '/damage-alerts' ? 'bg-red-100 text-red-600' :
                            item.path === '/expiry-alerts' ? 'bg-amber-100 text-amber-600' :
                            'bg-orange-100 text-orange-600'
                          }`}>
                            {item.badge}
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* ── Footer branding ── */}
        <div className="border-t border-gray-100 px-5 py-4 space-y-3">
          {canInstall && (
            <button
              onClick={install}
              title="Install App"
              className="flex w-full items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
            >
              <Download className="h-4 w-4" />
              <span className="sidebar-label">Install App</span>
            </button>
          )}
          <div className="sidebar-footer-copy">
            <p className="text-xs font-semibold text-gray-700">Pharmacy Management System</p>
            <p className="text-[10px] text-gray-400 mt-0.5">v1.0.0 · by Jahasoft.pk</p>
          </div>
        </div>
      </aside>
    </>
  );
}
